import React from 'react';

/**
 * Gyerekbarát, színes ikonok a főoldal gombjaihoz (beágyazott SVG, nincs szükség külső fájlra).
 * Az id-k megegyeznek a HomePage témáinak azonosítóival.
 */
type TopicIconProps = {
  id: string;
  size?: number;
  style?: React.CSSProperties;
};

const TopicIcon: React.FC<TopicIconProps> = ({ id, size = 32, style }) => {
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 48 48',
    'aria-hidden': true,
    focusable: false,
    style: { display: 'inline-block', flexShrink: 0, verticalAlign: 'middle', filter: 'none', ...style },
  } as const;

  switch (id) {
    // Magánóra – ceruza
    case 'maganora':
      return (
        <svg {...common}>
          <g transform="rotate(45 24 24)">
            <rect x="18" y="5" width="12" height="28" rx="2.5" fill="#FFC107" />
            <rect x="18" y="5" width="12" height="6" rx="2.5" fill="#FF6F91" />
            <rect x="18" y="11" width="12" height="2.5" fill="#B0BEC5" />
            <polygon points="18,33 30,33 24,44" fill="#FFE0B2" />
            <polygon points="21.8,39.2 26.2,39.2 24,44" fill="#5D4037" />
          </g>
        </svg>
      );

    // Gyermekagykontroll – mosolygó villanykörte
    case 'agykontroll':
      return (
        <svg {...common}>
          <circle cx="24" cy="20" r="14" fill="#FFD54F" stroke="#FFB300" strokeWidth="2" />
          <rect x="18" y="33" width="12" height="8" rx="2.5" fill="#90A4AE" />
          <rect x="20" y="41" width="8" height="3.5" rx="1.75" fill="#607D8B" />
          <circle cx="19" cy="18" r="1.8" fill="#5D4037" />
          <circle cx="29" cy="18" r="1.8" fill="#5D4037" />
          <path d="M18.5 23.5 Q24 29 29.5 23.5" fill="none" stroke="#5D4037" strokeWidth="2" strokeLinecap="round" />
          <circle cx="15.5" cy="22.5" r="2" fill="#FF8A80" opacity="0.7" />
          <circle cx="32.5" cy="22.5" r="2" fill="#FF8A80" opacity="0.7" />
        </svg>
      );

    // Tanulási tréning – rakéta
    case 'tanulas-trening':
      return (
        <svg {...common}>
          <path d="M16 31 L8 38 L17 36 Z" fill="#5C6BC0" />
          <path d="M32 31 L40 38 L31 36 Z" fill="#5C6BC0" />
          <path d="M24 3 C33 11 35 22 32 35 H16 C13 22 15 11 24 3 Z" fill="#FF7043" />
          <path d="M24 3 C28 7 30.5 11 31.5 15 H16.5 C17.5 11 20 7 24 3 Z" fill="#FFAB91" />
          <circle cx="24" cy="22" r="5" fill="#81D4FA" stroke="#fff" strokeWidth="2" />
          <path d="M19 35 H29 L24 45 Z" fill="#FFCA28" />
          <path d="M21.5 35 H26.5 L24 41 Z" fill="#FF7043" />
        </svg>
      );

    // Chillag – mosolygó csillag
    case 'csillag':
      return (
        <svg {...common}>
          <polygon
            points="24,4 29.5,17 43.5,18.5 33,27.8 36.2,41.5 24,34.3 11.8,41.5 15,27.8 4.5,18.5 18.5,17"
            fill="#FFD54F"
            stroke="#FFB300"
            strokeWidth="2.5"
            strokeLinejoin="round"
          />
          <circle cx="20.5" cy="25" r="1.7" fill="#5D4037" />
          <circle cx="27.5" cy="25" r="1.7" fill="#5D4037" />
          <path d="M20 29.5 Q24 33.5 28 29.5" fill="none" stroke="#5D4037" strokeWidth="2" strokeLinecap="round" />
        </svg>
      );

    // AI – barátságos robot
    case 'kapcsolat':
      return (
        <svg {...common}>
          <line x1="24" y1="14" x2="24" y2="7" stroke="#607D8B" strokeWidth="2.5" strokeLinecap="round" />
          <circle cx="24" cy="6" r="3.2" fill="#FF7043" />
          <rect x="6" y="21" width="4" height="9" rx="2" fill="#29B6F6" />
          <rect x="38" y="21" width="4" height="9" rx="2" fill="#29B6F6" />
          <rect x="9" y="14" width="30" height="25" rx="8" fill="#4FC3F7" stroke="#0288D1" strokeWidth="2" />
          <circle cx="18" cy="25" r="4.2" fill="#fff" />
          <circle cx="30" cy="25" r="4.2" fill="#fff" />
          <circle cx="18.6" cy="25.4" r="2" fill="#37474F" />
          <circle cx="30.6" cy="25.4" r="2" fill="#37474F" />
          <path d="M18.5 32.5 Q24 36.5 29.5 32.5" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" />
        </svg>
      );

    // Könyv – nyitott, mosolygós
    case 'konyv':
      return (
        <svg {...common}>
          <path d="M24 12 C19 8 11 8 5 10 V37 C11 35 19 35 24 39 Z" fill="#81D4FA" stroke="#0288D1" strokeWidth="2" strokeLinejoin="round" />
          <path d="M24 12 C29 8 37 8 43 10 V37 C37 35 29 35 24 39 Z" fill="#FFAB91" stroke="#E64A19" strokeWidth="2" strokeLinejoin="round" />
          <path d="M10 16 C14 15 18 15.5 21 17 M10 21 C14 20 18 20.5 21 22" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
          <circle cx="31" cy="21" r="1.6" fill="#5D4037" />
          <circle cx="37" cy="21" r="1.6" fill="#5D4037" />
          <path d="M31 26 Q34 29 37 26" fill="none" stroke="#5D4037" strokeWidth="2" strokeLinecap="round" />
        </svg>
      );

    // Célpont – színes, közepén szívvel
    case 'celpont':
      return (
        <svg {...common}>
          <circle cx="24" cy="25" r="18" fill="#FF8A80" stroke="#E53935" strokeWidth="2" />
          <circle cx="24" cy="25" r="12.5" fill="#fff" />
          <circle cx="24" cy="25" r="8" fill="#4FC3F7" />
          <circle cx="24" cy="25" r="3.6" fill="#FFD54F" />
          <path d="M24 25 L40 8" stroke="#8D6E63" strokeWidth="3" strokeLinecap="round" />
          <path d="M36 6 L42 6 L42 12 Z" fill="#66BB6A" />
        </svg>
      );

    // Agy – kedves, mosolygó
    case 'agy':
      return (
        <svg {...common}>
          <path d="M16 11 C9 11 6 17 8 22 C4 26 7 33 12 33 C13 38 19 40 22 36 V14 C21 12 19 11 16 11 Z" fill="#F48FB1" stroke="#D81B60" strokeWidth="2" strokeLinejoin="round" />
          <path d="M32 11 C39 11 42 17 40 22 C44 26 41 33 36 33 C35 38 29 40 26 36 V14 C27 12 29 11 32 11 Z" fill="#F8BBD0" stroke="#D81B60" strokeWidth="2" strokeLinejoin="round" />
          <circle cx="18" cy="24" r="1.8" fill="#5D4037" />
          <circle cx="30" cy="24" r="1.8" fill="#5D4037" />
          <path d="M19 29 Q24 34 29 29" fill="none" stroke="#5D4037" strokeWidth="2" strokeLinecap="round" />
          <path d="M14 18 Q17 16 19 18 M29 18 Q31 16 34 18" fill="none" stroke="#D81B60" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      );

    // Ajándék – masnis doboz
    case 'ajandek':
      return (
        <svg {...common}>
          <rect x="6" y="19" width="36" height="22" rx="3" fill="#AED581" stroke="#558B2F" strokeWidth="2" />
          <rect x="4" y="13" width="40" height="9" rx="3" fill="#FF8A80" stroke="#E53935" strokeWidth="2" />
          <rect x="21" y="13" width="6" height="28" fill="#FFD54F" />
          <path d="M24 13 C19 4 11 7 16 12 Z" fill="#FFD54F" stroke="#FFA000" strokeWidth="2" strokeLinejoin="round" />
          <path d="M24 13 C29 4 37 7 32 12 Z" fill="#FFD54F" stroke="#FFA000" strokeWidth="2" strokeLinejoin="round" />
        </svg>
      );

    // Beszélgetés – szívecskés buborék
    case 'beszed':
      return (
        <svg {...common}>
          <path d="M8 9 H40 A4 4 0 0 1 44 13 V29 A4 4 0 0 1 40 33 H24 L14 43 V33 H8 A4 4 0 0 1 4 29 V13 A4 4 0 0 1 8 9 Z" fill="#CE93D8" stroke="#8E24AA" strokeWidth="2" strokeLinejoin="round" />
          <path d="M24 28 C14 21 17 14 21.5 15.5 C23 16 24 17.5 24 17.5 C24 17.5 25 16 26.5 15.5 C31 14 34 21 24 28 Z" fill="#FF5252" />
        </svg>
      );

    default:
      return (
        <svg {...common}>
          <polygon
            points="24,4 29.5,17 43.5,18.5 33,27.8 36.2,41.5 24,34.3 11.8,41.5 15,27.8 4.5,18.5 18.5,17"
            fill="#FFD54F"
          />
        </svg>
      );
  }
};

export default TopicIcon;